import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useSeasons, useGame, useUpdateGameResult, useAdminUsersList } from '../hooks/useScoreboard';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import { MONTHS, DAYS, buildGameDate, splitGameDate } from '../lib/gameDate';
import { basePoints } from '../lib/roles';
import type { Role, CreateGameResultPlayer } from '../types/scoreboard';

const ROLES: Role[] = ['мирный', 'мафия', 'дон', 'шериф'];
const MANUAL = '__manual__';

interface PlayerRowState {
  selectedUserId: string; // '' = nothing chosen, MANUAL = typed name, otherwise a real userId
  name: string;
  role: Role;
  points: string;
}

function emptyRow(): PlayerRowState {
  return { selectedUserId: '', name: '', role: 'мирный', points: '' };
}

export function EditGameResultPage() {
  const navigate = useNavigate();
  const { seasonId, gameId } = useParams<{ seasonId: string; gameId: string }>();
  const { user } = useAuthStore();
  const { data: seasons } = useSeasons();
  const { data: game, isLoading: gameLoading, isError: gameError } = useGame(seasonId ?? '', gameId ?? '');
  const { data: users } = useAdminUsersList();
  const updateGameResult = useUpdateGameResult(seasonId ?? '', gameId ?? '');

  const [gameDay, setGameDay] = useState('');
  const [gameMonth, setGameMonth] = useState('');
  const [gameNumber, setGameNumber] = useState('');
  const [judge, setJudge] = useState('');
  const [winnerTeam, setWinnerTeam] = useState<'red' | 'black'>('red');
  const [players, setPlayers] = useState<PlayerRowState[]>(
    Array.from({ length: 10 }, emptyRow),
  );
  const [error, setError] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (game && !prefilled) {
      const { day, month } = splitGameDate(game.gameDate);
      setGameDay(day);
      setGameMonth(month);
      setGameNumber(game.gameNumber);
      setJudge(game.judge);
      setWinnerTeam(game.winnerTeam);
      setPlayers(
        game.players.map((p) => ({
          selectedUserId: p.userId ?? MANUAL,
          name: p.displayName,
          role: p.role,
          points: String(p.points),
        })),
      );
      setPrefilled(true);
    }
  }, [game, prefilled]);

  if (!isFreeAdmin(user?.email)) {
    return (
      <PageWrapper>
        <p className="text-mafia-paper-dim">Нет доступа.</p>
      </PageWrapper>
    );
  }

  if (!seasonId || !gameId) {
    return (
      <PageWrapper>
        <p className="text-mafia-paper-dim">Игра не найдена.</p>
      </PageWrapper>
    );
  }

  if (gameLoading) {
    return (
      <PageWrapper>
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-mafia-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  if (gameError || !game) {
    return (
      <PageWrapper>
        <p className="text-mafia-paper-dim">Игра не найдена.</p>
        <Link to="/scoreboard" className="text-mafia-gold hover:underline mt-4 inline-block font-mono text-sm">
          ← Турнирная таблица
        </Link>
      </PageWrapper>
    );
  }

  const season = seasons?.find((s) => s.seasonId === seasonId);
  const seasonName = season?.name ?? seasonId;
  const seasonYear = season?.startDate.split('-')[0] ?? '';

  function updateRow(idx: number, patch: Partial<PlayerRowState>) {
    setPlayers((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  }

  function handleWinnerTeamChange(team: 'red' | 'black') {
    setWinnerTeam(team);
    setPlayers((prev) => prev.map((row) => ({ ...row, points: basePoints(row.role, team) })));
  }

  function handleRoleChange(idx: number, role: Role) {
    updateRow(idx, { role, points: basePoints(role, winnerTeam) });
  }

  function handlePlayerSelect(idx: number, value: string) {
    if (value === MANUAL) {
      updateRow(idx, { selectedUserId: MANUAL, name: '' });
      return;
    }
    const u = users?.find((u) => u.userId === value);
    updateRow(idx, { selectedUserId: value, name: u?.displayName ?? '' });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!seasonYear) {
      setError('Не удалось определить год сезона.');
      return;
    }
    if (!gameDay || !gameMonth || !gameNumber.trim() || !judge.trim()) {
      setError('Заполните дату игры, номер игры и ведущего.');
      return;
    }
    const resolvedPlayers: CreateGameResultPlayer[] = [];
    for (let i = 0; i < players.length; i++) {
      const row = players[i];
      if (!row.selectedUserId) continue; // row left unselected — game had fewer than 10 players
      if (!row.name.trim()) {
        setError(`У игрока в строке ${i + 1} должно быть имя.`);
        return;
      }
      const points = parseFloat(row.points);
      if (Number.isNaN(points)) {
        setError('Баллы должны быть числом для каждого игрока.');
        return;
      }
      if (Math.abs(points * 10 - Math.round(points * 10)) > 1e-9) {
        setError(`Баллы должны иметь не более 1 знака после запятой (${row.name || `игрок #${i + 1}`}: ${row.points}).`);
        return;
      }
      resolvedPlayers.push({
        userId: row.selectedUserId === MANUAL ? undefined : row.selectedUserId,
        name: row.name.trim(),
        role: row.role,
        points,
      });
    }
    if (resolvedPlayers.length < 8) {
      setError('Должно быть заполнено минимум 8 игроков (можно оставить незаполненными максимум 2 строки).');
      return;
    }

    try {
      await updateGameResult.mutateAsync({
        gameDate: buildGameDate(seasonYear, gameMonth, gameDay),
        gameNumber: gameNumber.trim(),
        judge: judge.trim(),
        winnerTeam,
        players: resolvedPlayers,
      });
      navigate('/scoreboard');
    } catch (err: unknown) {
      setError((err as Error).message || 'Не удалось сохранить результаты игры.');
    }
  }

  return (
    <PageWrapper className="max-w-4xl">
      <Link to="/scoreboard" className="font-mono text-xs text-mafia-muted hover:text-mafia-gold tracking-wider uppercase transition-colors mb-8 inline-block">
        ← Турнирная таблица
      </Link>

      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">◆ Админ ◆</p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-2">Редактировать результаты игры</h1>
      <p className="font-mono text-xs text-mafia-muted tracking-wider uppercase mb-8">Сезон: {seasonName}</p>

      <OrnamentDivider className="mb-8" />

      <form onSubmit={handleSubmit} className="space-y-7">
        {/* Игра */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2 block">
              Дата игры *
            </label>
            <div className="flex gap-2">
              <select
                value={gameDay}
                onChange={(e) => setGameDay(e.target.value)}
                className="w-20 bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-3 focus:outline-none focus:border-mafia-gold transition-colors"
              >
                <option value="">День</option>
                {DAYS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select
                value={gameMonth}
                onChange={(e) => setGameMonth(e.target.value)}
                className="flex-1 bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-3 focus:outline-none focus:border-mafia-gold transition-colors"
              >
                <option value="">Месяц</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <p className="text-mafia-muted text-xs font-mono mt-1">
              {seasonYear ? `Год сезона: ${seasonYear}` : ''}
            </p>
          </div>
          <div>
            <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2 block">
              Номер игры *
            </label>
            <input
              type="text"
              value={gameNumber}
              onChange={(e) => setGameNumber(e.target.value)}
              placeholder="1"
              required
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-3 focus:outline-none focus:border-mafia-gold transition-colors placeholder:text-mafia-muted"
            />
          </div>
          <div>
            <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2 block">
              Ведущий *
            </label>
            <input
              type="text"
              value={judge}
              onChange={(e) => setJudge(e.target.value)}
              placeholder="Канцлер"
              required
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-3 focus:outline-none focus:border-mafia-gold transition-colors placeholder:text-mafia-muted"
            />
          </div>
        </div>

        {/* Победитель */}
        <div>
          <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2 block">
            Победитель *
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleWinnerTeamChange('red')}
              className={`flex-1 py-2.5 text-sm font-mono border transition-colors ${
                winnerTeam === 'red'
                  ? 'border-mafia-gold text-mafia-gold bg-mafia-gold/10'
                  : 'border-mafia-border text-mafia-paper-dim hover:text-mafia-paper'
              }`}
            >
              Мирные
            </button>
            <button
              type="button"
              onClick={() => handleWinnerTeamChange('black')}
              className={`flex-1 py-2.5 text-sm font-mono border transition-colors ${
                winnerTeam === 'black'
                  ? 'border-mafia-crimson-light text-mafia-crimson-light bg-mafia-crimson/10'
                  : 'border-mafia-border text-mafia-paper-dim hover:text-mafia-paper'
              }`}
            >
              Мафия
            </button>
          </div>
        </div>

        {/* Игроки */}
        <div>
          <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-3 block">
            Игроки (8–10) *
          </label>
          <p className="text-mafia-muted text-xs font-mono mb-3">
            Чтобы убрать игрока, нажмите «⇄» в его строке и оставьте «— выбрать игрока —».
          </p>
          <div className="space-y-2">
            {players.map((row, idx) => (
              <div key={idx} className="grid grid-cols-[2rem_1fr_7rem_6rem] gap-2 items-center">
                <span className="text-mafia-muted text-sm font-mono text-center">{idx + 1}</span>

                {row.selectedUserId === MANUAL ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => updateRow(idx, { name: e.target.value })}
                      placeholder="Имя игрока"
                      className="flex-1 min-w-0 bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2.5 focus:outline-none focus:border-mafia-gold transition-colors placeholder:text-mafia-muted"
                    />
                    <button
                      type="button"
                      onClick={() => updateRow(idx, { selectedUserId: '', name: '' })}
                      title="Выбрать из списка"
                      className="shrink-0 text-mafia-muted hover:text-mafia-gold text-xs font-mono border border-mafia-border px-2"
                    >
                      ⇄
                    </button>
                  </div>
                ) : (
                  <select
                    value={row.selectedUserId}
                    onChange={(e) => handlePlayerSelect(idx, e.target.value)}
                    className="bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2.5 focus:outline-none focus:border-mafia-gold transition-colors"
                  >
                    <option value="">— выбрать игрока —</option>
                    {users?.map((u) => (
                      <option key={u.userId} value={u.userId}>{u.displayName}</option>
                    ))}
                    <option value={MANUAL}>— ввести вручную —</option>
                  </select>
                )}

                <select
                  value={row.role}
                  onChange={(e) => handleRoleChange(idx, e.target.value as Role)}
                  className="bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2.5 focus:outline-none focus:border-mafia-gold transition-colors"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>

                <input
                  type="number"
                  step="0.1"
                  value={row.points}
                  onChange={(e) => updateRow(idx, { points: e.target.value })}
                  placeholder="Баллы"
                  className="bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2.5 focus:outline-none focus:border-mafia-gold transition-colors placeholder:text-mafia-muted"
                />
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-mafia-crimson-light font-mono text-sm">{error}</p>}

        <div className="flex gap-4 pt-2">
          <button
            type="submit"
            disabled={updateGameResult.isPending}
            className="btn-gold-solid flex-1 py-3 text-sm disabled:opacity-50"
          >
            {updateGameResult.isPending ? 'Сохраняем...' : 'Сохранить изменения'}
          </button>
          <Link
            to="/scoreboard"
            className="flex-1 py-3 text-sm text-center font-mono border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors"
          >
            Отмена
          </Link>
        </div>
      </form>
    </PageWrapper>
  );
}
